/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomerAccount, InvestmentDetails, InvestmentPlan, StrategyInfo, StrategyStock, StrategyStockAllocationEve2 } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import * as fromAppRoot from '@app/app.reducers';

import {
  AccountExtraData,
  Company,
  CompanyListItem,
  CriteriumData,
  CustomerInvestmentData,
  GenerateStrategy,
  GetStrategy,
  RiskAnswer,
  RiskSurveyQuestion,
  SearchCompany,
  StrategyStockAllocation,
  TopicData
} from '@shared/models/Strategy';

import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StrategyService {
  accountID: string;

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentAccount)).subscribe((value) => {
      this.accountID = value.id;
    });
  }

  getStrategyEditMode(): Observable<{ disabled: boolean }> {
    return this.http.get<{ disabled: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/edit-mode`);
  }

  getTopics(): Observable<{ customer: { topics: TopicData[] }; topics: TopicData[] }> {
    return this.http.get<{ customer: { topics: TopicData[] }; topics: TopicData[] }>(`${environment.apiUrl}/accounts/${this.accountID}/topics`);
  }

  getCriteria(): Observable<{ customer: { criteria: CriteriumData[] }; criteria: CriteriumData[] }> {
    return this.http.get<{ customer: { criteria: CriteriumData[] }; criteria: CriteriumData[] }>(
      `${environment.apiUrl}/accounts/${this.accountID}/criteria`
    );
  }

  getMatches(): Observable<{ blacklist: CompanyListItem[]; wishlist: CompanyListItem[]; yova_stocks: CompanyListItem[] }> {
    return this.http.get<{ blacklist: CompanyListItem[]; wishlist: CompanyListItem[]; yova_stocks: CompanyListItem[] }>(
      `${environment.apiUrl}/accounts/${this.accountID}/strategy/matches`
    );
  }

  getCompanyDetails(companyId: number): Observable<{ company: Company }> {
    return this.http.get<{ company: Company }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/company/${companyId}`);
  }

  getCustomerInvestment(): Observable<{ customer: CustomerInvestmentData }> {
    return this.http.get<{ customer: CustomerInvestmentData }>(`${environment.apiUrl}/accounts/${this.accountID}/profiling`);
  }

  getCustomerDiscardedWishes(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/wishes/discarded`);
  }

  getRiskQuestions(): Observable<{ survey_questions: RiskSurveyQuestion[] }> {
    return this.http.get<{ survey_questions: RiskSurveyQuestion[] }>(`${environment.apiUrl}/accounts/${this.accountID}/survey/risk`);
  }

  getMifidQeustions() {
    return this.http.get<{ survey_questions: RiskSurveyQuestion[] }>(`${environment.apiUrl}/accounts/${this.accountID}/survey/mifid2`);
  }

  setMifidQeustions(body): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/survey/mifid2/answers`, body);
  }

  getStrategy(): Observable<GetStrategy> {
    return this.http.get<GetStrategy>(`${environment.apiUrl}/accounts/${this.accountID}/strategy`);
  }

  updateStrategy(
    body:
      | {
          one_time_investment: number;
          period_investment: number;
          risk_answers: RiskAnswer[];
        }
      | { topics: number[]; criteria: number[] }
      | { one_time_investment: number; period_investment: number }
      | {
          company_whitelist: number[];
          company_blacklist: number[];
        }
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/update`, body);
  }

  updateInvestmentPlan(body: { initial_investment: number; monthly_investment: number }): Observable<{ investment_plan: InvestmentPlan }> {
    return this.http.put<{ investment_plan: InvestmentPlan }>(`${environment.apiUrlV4}/onboarding/accounts/${this.accountID}/investment_plan`, body);
  }

  getInvestmentPlan(): Observable<{ investment_plan: InvestmentPlan }> {
    return this.http.get<{ investment_plan: InvestmentPlan }>(`${environment.apiUrlV4}/onboarding/accounts/${this.accountID}/investment_plan`);
  }

  getStrategyInfo(): Observable<{ strategy_info: StrategyInfo }> {
    return this.http.get<{ strategy_info: StrategyInfo }>(`${environment.apiUrlV4}/reporting/accounts/${this.accountID}/strategy_info`);
  }

  getFundBreakdown(): Observable<{ stocks: StrategyStock[] }> {
    return this.http.get<{ stocks: StrategyStock[] }>(`${environment.apiUrlV4}/reporting/accounts/${this.accountID}/fund_breakdown`);
  }

  getInvestmentDetails(): Observable<InvestmentDetails> {
    return this.http
      .get<{ strategy: InvestmentDetails }>(`${environment.apiUrlV4}/trading_prep/accounts/${this.accountID}/strategy`)
      .pipe(map(({ strategy }) => strategy));
  }

  updateStrategyEditMode(body: { edit_mode: boolean }): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/edit-mode`, body);
  }

  discardStrategyChanges(body = {}): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/discard`, body);
  }

  saveStrategyChanges(body = {}): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/edit-mode`, body);
  }

  generateStrategy(body = {}): Observable<GenerateStrategy> {
    return this.http.post<GenerateStrategy>(`${environment.apiUrl}/accounts/${this.accountID}/strategy`, body);
  }

  updateRiskRecheck(body: { risk_answers: RiskAnswer[] }): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/risk`, body);
  }

  updateRisk(body: { risk_answers: RiskAnswer[]; selected_stock_split: number }): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(`${environment.apiUrlV4}/onboarding/accounts/${this.accountID}/risk_profile`, body);
  }

  undoStrategyActions(body = {}): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/undo`, body);
  }

  getRiskStockSplit(body: { body: RiskAnswer[] }): Observable<{ stock_split: number; max_stock_split: number }> {
    return this.http.post<{ stock_split: number; max_stock_split: number }>(
      `${environment.apiUrl}/accounts/${this.accountID}/strategy/stock-split`,
      body
    );
  }

  searchCompanies(searchedTerm: string): Observable<{ companies: SearchCompany[] }> {
    const params = new HttpParams().set('q', searchedTerm);
    return this.http.get<{ companies: SearchCompany[] }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/company/search`, {
      params
    });
  }

  getStockAllocation(): Observable<{ data: { id: string; type: string }; meta: StrategyStockAllocation }> {
    return this.http.get<{ data: { id: string; type: string }; meta: StrategyStockAllocation }>(
      `${environment.apiUrlV3}/target_portfolios/${this.accountID}?sector_percentages=true&currency_percentages=true`
    );
  }

  getStockAllocationEve2(): Observable<{ stock_allocation: StrategyStockAllocationEve2 }> {
    return this.http.get<{ stock_allocation: StrategyStockAllocationEve2 }>(
      `${environment.apiUrlV4}/reporting/accounts/${this.accountID}/stock_allocation`
    );
  }

  disableInterviewNotification(customerID: number) {
    return this.http.put<{ data: { type: string; id: number; attributes: { interview_widget: boolean } } }>(
      `${environment.apiUrlV3}/customers/${customerID}`,
      {
        data: { type: 'customers', id: customerID, attributes: { interview_widget: false } }
      }
    );
  }

  updateToAlmostProspect(): Observable<CustomerAccount['customer_journey']> {
    return this.http.put<CustomerAccount['customer_journey']>(`${environment.apiUrl}/accounts/${this.accountID}/customer_journey`, {
      customer_journey: { ql_flow_completed: true }
    });
  }

  getAccountExtraDetail(): Observable<AccountExtraData> {
    return this.http.get<AccountExtraData>(`${environment.apiUrlV3}/accounts/${this.accountID}`);
  }

  putAccountExtraDetail(payload): Observable<AccountExtraData> {
    return this.http.put<AccountExtraData>(`${environment.apiUrlV3}/accounts/${this.accountID}`, payload);
  }

  updateQLOnboardingStep(ql_onboarding_step: number) {
    const payload = {
      data: {
        type: 'accounts',
        id: this.accountID,
        attributes: {
          ql_onboarding_step: ql_onboarding_step
        }
      }
    };
    return this.putAccountExtraDetail(payload);
  }

  updateKidsAccountStep(): Observable<{
    data: {
      attributes: { account_data_filled: boolean; id_verification_completed: boolean };
    };
  }> {
    return this.http.post<{ data: { attributes: { account_data_filled: boolean; id_verification_completed: boolean } } }>(
      `${environment.apiUrlV3}/kids_account_confirmations/${this.accountID}`,
      {}
    );
  }

  updateKidsAccountDetail({ owner_name, gender, year_of_birth }) {
    const payload = {
      data: {
        type: 'accounts',
        id: this.accountID,
        attributes: {
          owner_name,
          extra_data: {
            gender,
            year_of_birth
          }
        }
      }
    };

    return this.putAccountExtraDetail(payload);
  }

  skipSaxoOnboarding(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrlV4}/onboarding/saxo_accounts/${this.accountID}/open`, {});
  }

  dismissEve2Modal(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrlV4}/onboarding/accounts/${this.accountID}/eve2_modal_dismissal`, {});
  }
}
